import * as React from "react"
import { Check, ChevronDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Button } from "./button"
import { Badge } from "./badge"
import { Checkbox } from "./checkbox"

const MultiSelect = React.forwardRef(({ 
  options = [], 
  value = [], 
  onValueChange, 
  placeholder = "Seleziona...",
  className,
  maxDisplay = 2,
  ...props 
}, ref) => {
  const [open, setOpen] = React.useState(false)

  const handleToggle = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue]
    onValueChange(newValue)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onValueChange([])
  }

  const handleRemove = (e, optionValue) => {
    e.stopPropagation()
    onValueChange(value.filter(v => v !== optionValue))
  }

  const selectedLabels = options
    .filter(opt => value.includes(opt.value))
    .map(opt => opt.label)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "min-w-[150px] justify-between font-normal",
            !value.length && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <div className="flex items-center gap-1 flex-1 overflow-hidden">
            {value.length === 0 ? (
              <span>{placeholder}</span>
            ) : value.length <= maxDisplay ? (
              <div className="flex flex-wrap gap-1">
                {selectedLabels.map((label, i) => (
                  <Badge 
                    key={i} 
                    variant="secondary" 
                    className="text-xs px-1.5 py-0"
                  >
                    {label}
                    <X 
                      className="ml-1 h-3 w-3 cursor-pointer hover:text-destructive" 
                      onClick={(e) => handleRemove(e, value[i])}
                    />
                  </Badge>
                ))}
              </div>
            ) : (
              <Badge variant="secondary" className="text-xs">
                {value.length} selezionati
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2">
            {value.length > 0 && (
              <X 
                className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer" 
                onClick={handleClear}
              />
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="max-h-[300px] overflow-y-auto">
          {options.length === 0 ? (
            <div className="py-2 text-center text-sm text-muted-foreground">
              Nessuna opzione
            </div>
          ) : (
            options.map((option) => (
              <div
                key={option.value}
                className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent rounded-sm"
                onClick={() => handleToggle(option.value)}
              >
                <Checkbox
                  checked={value.includes(option.value)}
                  onCheckedChange={() => handleToggle(option.value)}
                />
                <span className="text-sm flex-1">{option.label}</span>
                {value.includes(option.value) && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </div>
            ))
          )}
        </div>
        {value.length > 0 && (
          <div className="border-t mt-2 pt-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs"
              onClick={() => onValueChange([])}
            >
              Cancella selezione
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
})

MultiSelect.displayName = "MultiSelect"

export { MultiSelect }
